import { mdiCheck, mdiClose } from '@mdi/js';
import type { MappedCustomDataServiceObjectField, SelectValue } from 'common/models';
import Icon from 'components/Icon/Icon';
import LabeledText from 'components/LabeledText/LabeledText';
import dayjs from 'dayjs';
import { toLocaleString } from 'helpers/mathHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import React from 'react';

import CustomDataCheckList from '../CustomDataCheckList';
import CustomDataGeoCoordinate from '../CustomDataGeoCoordinate';

interface Props {
  fieldData: MappedCustomDataServiceObjectField;
  serviceObjectId: string;
}

const ServiceObjectLabeledText = (props: Props) => {
  const { fieldData, serviceObjectId } = props;
  const { i18n } = useCustomTranslation();

  const fieldValue = fieldData.values.find((x) => x.serviceObjectId === serviceObjectId)?.value;

  if (!fieldValue) return null;

  switch (fieldData.formField.type) {
    case 'DateTime':
      return (
        fieldValue.value && (
          <LabeledText
            label={fieldData.label}
            value={dayjs
              .utc(fieldValue.value)
              .locale(i18n.language)
              .format(fieldData.format || 'LLL')}
          />
        )
      );
    case 'Date':
      return (
        fieldValue.value && (
          <LabeledText
            label={fieldData.label}
            value={dayjs
              .utc(fieldValue.value)
              .locale(i18n.language)
              .format(fieldData.format || 'LL')}
          />
        )
      );
    case 'Time':
      return (
        fieldValue.value && (
          <LabeledText
            label={fieldData.label}
            value={dayjs
              .utc(fieldValue.value)
              .locale(i18n.language)
              .format(fieldData.format || 'LT')}
          />
        )
      );
    case 'Boolean':
      return (
        <LabeledText
          label={fieldData.label}
          value={
            fieldValue ? (
              <Icon path={mdiCheck} className="text-success" />
            ) : (
              <Icon path={mdiClose} className="text-danger" />
            )
          }
        />
      );
    case 'Number': {
      const value =
        fieldValue.value || fieldValue.value === 0
          ? toLocaleString(fieldValue.value * (fieldData?.multiplier || 1))
          : '';

      return (
        <LabeledText
          label={fieldData.label}
          value={
            fieldData.formField.unit && fieldValue.value
              ? `${value} ${fieldData.formField.unit}`
              : value
          }
        />
      );
    }
    case 'Select':
      if (fieldData.formField.isMultiSelect) {
        return (
          <LabeledText
            label={fieldData.label}
            value={fieldValue.value?.map((value: SelectValue) => value.stringValue).join(', ')}
          />
        );
      }

      return <LabeledText label={fieldData.label} value={fieldValue.stringValue} />;
    case 'CheckList':
      return (
        <CustomDataCheckList
          title={fieldData.label}
          data={fieldValue.value}
          checkListProps={{
            falseLabel: fieldData.formField.falseLabel,
            itemLabel: fieldData.formField.itemLabel,
            options: fieldData.formField.options,
            requireDescriptionWhenFalse: fieldData.formField.requireDescriptionWhenFalse,
            requireDescriptionWhenTrue: fieldData.formField.requireDescriptionWhenTrue,
            showIndex: fieldData.formField.showIndex,
            trueLabel: fieldData.formField.trueLabel,
            descriptionLabel: fieldData.formField.descriptionLabel
          }}
        />
      );
    case 'GeoCoordinate':
      return <CustomDataGeoCoordinate label={fieldData.label} location={fieldValue.value} />;
    default:
      return <LabeledText label={fieldData.label} value={fieldValue.value} />;
  }
};

export default ServiceObjectLabeledText;
